import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Checkbox,
  Paper,
  Button,
  IconButton,
  Stack,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { getTimeSlotError, isStopOnMatchEnabled } from "./RuleValidation";
import { createEmptySlot, toHHMMSS } from "./RuleUtils";

const Section = ({ title, children }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Paper>
  );
};

const DAY_OPTIONS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

const getAllowedDayValuesByRange = (startDate, endDate) => {
  const start = startDate ? moment(startDate, "YYYY-MM-DD", true) : null;
  const end = endDate ? moment(endDate, "YYYY-MM-DD", true) : null;

  if (!start?.isValid() || !end?.isValid() || end.isBefore(start)) {
    return DAY_OPTIONS.map((day) => day.value);
  }
  const dayCount = end.diff(start, "days") + 1;
  if (dayCount >= 7) {
    return DAY_OPTIONS.map((day) => day.value);
  }

  const availableDays = new Set();
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, "day")) {
    availableDays.add(cursor.isoWeekday());
    cursor.add(1, "day");
  }

  return DAY_OPTIONS.map((day) => day.value).filter((value) => availableDays.has(value));
};

const PRIORITY_RANGES = {
  FLASH_SALE: { min: 51, max: 100 },
  DISCOUNTED_PRICING: { min: 101, max: 500 },
};
const DEFAULT_PRIORITY = 1;
const MAX_DATE_RANGE_DAYS = 30;

const parseFrequencyToDays = (frequency) => {
  if (!frequency) return [];

  let parsed = frequency;

  if (typeof frequency === "string") {
    try {
      parsed = JSON.parse(frequency);
    } catch {
      return [];
    }
  }

  const days = Array.isArray(parsed) ? parsed : parsed?.daysOfWeek;
  if (!Array.isArray(days)) return [];

  return days.map((d) => ({
    day: Number(d?.day) || 1,
    timeSlots: Array.isArray(d?.timeSlots) && d.timeSlots.length
      ? d.timeSlots.map((s) => ({
        start: toHHMMSS(s?.start || ""),
        end: toHHMMSS(s?.end || ""),
      }))
      : [createEmptySlot()],
  }));
};

export default function GeneralDetails({ form, setField, errors, enums, configs }) {
  const ruleTypes = enums?.RuleType ? Object.keys(enums.RuleType) : [];
  const showStopOnMatch = isStopOnMatchEnabled(configs);
  const [dateError, setDateError] = useState(null);
  const [frequencyMode, setFrequencyMode] = useState("custom");

  const allowedDayValues = useMemo(
    () => getAllowedDayValuesByRange(form.startDate, form.endDate),
    [form.startDate, form.endDate]
  );

  const daysOfWeek = useMemo(
    () => parseFrequencyToDays(form.frequency),
    [form.frequency]
  );

  const allDaysMode = frequencyMode === "all";
  const allDaysUsed = daysOfWeek.length >= allowedDayValues.length;

  useEffect(() => {
    if (!allowedDayValues.length || !daysOfWeek.length) return;
    const selected = new Set(daysOfWeek.map((d) => Number(d.day)));
    const isAllSelected =
      allowedDayValues.every((d) => selected.has(d)) &&
      selected.size === allowedDayValues.length;
    setFrequencyMode(isAllSelected ? "all" : "custom");
  }, [daysOfWeek, allowedDayValues]);

  const syncFrequency = (nextDays) => {
    setField("frequency", { daysOfWeek: nextDays });
  };

  const updateFrequency = (updater) => {
    const next = updater(daysOfWeek);
    setField("frequency", { daysOfWeek: next });
  };

  const removeDay = (dayIndex) => {
    const next = daysOfWeek.filter((_, i) => i !== dayIndex);
    syncFrequency(next);
  };

  const addDay = () => {
    updateFrequency((prev) => {
      const used = new Set(prev.map((d) => Number(d.day)));
      const firstAvailable = allowedDayValues.find((v) => !used.has(v));
      if (!firstAvailable) return prev;

      return [
        ...prev,
        { day: firstAvailable, timeSlots: [createEmptySlot()] },
      ];
    });
  };

  const updateDay = (index, value) => {
    const nextDay = Number(value);

    updateFrequency((prev) => {
      if (prev.some((d, i) => i !== index && Number(d.day) === nextDay)) {
        return prev;
      }

      const next = [...prev];
      next[index] = { ...next[index], day: nextDay };
      return next;
    });
  };

  const addSlot = (dayIndex) => {
    const targetSlots = allDaysMode
      ? daysOfWeek[0]?.timeSlots || []
      : daysOfWeek[dayIndex]?.timeSlots || [];

    const hasIncompleteSlot = targetSlots.some(
      (slot) => !slot?.start?.toString().trim() || !slot?.end?.toString().trim()
    );

    if (hasIncompleteSlot) return;
    const next = [...daysOfWeek];
    if (allDaysMode) {
      const updatedAllDays = next.map((day) => ({
        ...day,
        timeSlots: [...day.timeSlots, createEmptySlot()],
      }));
      syncFrequency(updatedAllDays);
      return;
    }
    next[dayIndex] = {
      ...next[dayIndex],
      timeSlots: [...next[dayIndex].timeSlots, createEmptySlot()],
    };
    syncFrequency(next);
  };

  const removeSlot = (dayIndex, slotIndex) => {
    updateFrequency((prev) =>
      prev.map((day, i) => {
        if (allDaysMode || i === dayIndex) {
          const slots = day.timeSlots.filter((_, s) => s !== slotIndex);
          return {
            ...day,
            timeSlots: slots.length ? slots : [createEmptySlot()],
          };
        }
        return day;
      })
    );
  };

  const updateSlot = (dayIndex, slotIndex, key, value) => {
    const normalized = toHHMMSS(value);

    updateFrequency((prev) =>
      prev.map((day, i) => {
        if (allDaysMode || i === dayIndex) {
          const slots = [...day.timeSlots];
          slots[slotIndex] = { ...slots[slotIndex], [key]: normalized };
          return { ...day, timeSlots: slots };
        }
        return day;
      })
    );
  };

  const validateDates = (field, value) => {
    const today = moment().startOf("day");
    const start = form.startDate ? moment(form.startDate) : null;
    const end = form.endDate ? moment(form.endDate) : null;

    let newErrors = { ...errors };

    if (field === "startDate") {
      if (value && moment(value).isBefore(today)) {
        newErrors.startDate = "Date should be today or greater";
      } else {
        delete newErrors.startDate;
        setField("startDate", value);
      }

      if (end && moment(value).isAfter(end)) {
        newErrors.startDate = "Start date must be before End date";
      } else if (end && !form.id && end.diff(moment(value), "days") > MAX_DATE_RANGE_DAYS) {
        newErrors.startDate = `Start date and End date cannot be more than ${MAX_DATE_RANGE_DAYS} days apart`;
      }
    }

    if (field === "endDate") {
      if (start && moment(value).isBefore(start)) {
        newErrors.endDate = "End date cannot be less than Start date";
      } else if (value && moment(value).isBefore(today)) {
        newErrors.endDate = "Date should be today or greater";
      } else if (start && !form.id && moment(value).diff(start, "days") > MAX_DATE_RANGE_DAYS) {
        newErrors.endDate = `Start date and End date cannot be more than ${MAX_DATE_RANGE_DAYS} days apart`;
      } else if (form.id && form.originalEndDate && Math.abs(moment(value).diff(moment(form.originalEndDate), "days")) > MAX_DATE_RANGE_DAYS) {
        newErrors.endDate = `End date cannot be changed by more than ${MAX_DATE_RANGE_DAYS} days from the original End date`;
      } else {
        delete newErrors.endDate;
        setField("endDate", value);
      }
    }
    setDateError(newErrors);
  };

  const setRuleType = (value) => {
    setField("ruleType", value);
    setField('frequency', null);
    const range = PRIORITY_RANGES[value];
    setField("priority", range ? range.min : DEFAULT_PRIORITY);
  }

  const priorityRange = PRIORITY_RANGES[form.ruleType];
  const isPriorityEditable = !!priorityRange;

  const handlePriorityChange = (value) => {
    if (value === "") {
      setField("priority", "");
      return;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return;
    setField("priority", numeric);
  };

  const selectAllDays = () => {
    const sharedSlots =
      daysOfWeek[0]?.timeSlots?.length
        ? daysOfWeek[0].timeSlots
        : [createEmptySlot()];

    const next = allowedDayValues.map((day) => ({
      day,
      timeSlots: sharedSlots.map((s) => ({ ...s })),
    }));

    setFrequencyMode("all");
    setField("frequency", { daysOfWeek: next });
  };

  const clearAllDays = () => {
    setFrequencyMode("custom");
    syncFrequency([]);
  };

  const onFrequencyModeChange = (value) => {
    if (value === "all") {
      selectAllDays();
      return;
    }

    setFrequencyMode("custom");
    if (allDaysMode) {
      const firstDay = daysOfWeek[0];
      syncFrequency(firstDay ? [{ ...firstDay }] : []);
    }
  };

  const today = moment().startOf("day");
  const start = form.startDate ? moment(form.startDate) : null;
  const end = form.endDate ? moment(form.endDate) : null;

  const isPublished = form?.ruleJson?.publishedCommitId;

  const isUpcoming = start && start.isAfter(today);
  const isOngoing = start && end && start.isSameOrBefore(today) && end.isAfter(today);

  const disableStartDate = isPublished && !isUpcoming;
  const disableEndDate = isPublished && !(isUpcoming || isOngoing);

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 2 }}>
          Step 1: General Rule Details
        </Typography>


        {/* ===================== RULE DETAILS ===================== */}
        <Section title="Rule Details">
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Rule Type
              </Typography>

              <FormControl fullWidth size="small" error={!!errors?.ruleType}>
                <Select
                  value={form.ruleType}
                  onChange={(e) => { if (!form.id) setRuleType(e.target.value); }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a rule type</em>
                  </MenuItem>

                  {ruleTypes.filter(val => val != "LIQUIDATION_SALE").map((key) => (
                    <MenuItem key={key} value={key}>
                      {enums.RuleType[key]}
                    </MenuItem>
                  ))}
                </Select>

                {errors?.ruleType && (
                  <Typography sx={{ fontSize: 12, mt: 0.5, color: "error.main" }}>
                    {errors.ruleType}
                  </Typography>
                )}
              </FormControl>


            </Grid>

            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={form.masterDisable}
                    onChange={(e) => setField("masterDisable", e.target.checked)}
                  />
                }
                label="Master Disable (Globally disable or enable the rule instantly)"
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Title
              </Typography>

              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                slotProps={{
                  input: {
                    inputProps: {
                      maxLength: 50,
                    },
                  },
                }}


              />

              <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 0.5 }}>
                Max 50 characters.
              </Typography>
            </Grid>
            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Priority
              </Typography>

              <TextField
                size="small"
                fullWidth
                type="number"
                value={form.priority}
                disabled={!isPriorityEditable}
                onChange={(e) => handlePriorityChange(e.target.value)}
                error={!!errors?.priority}
                helperText={
                  errors?.priority ||
                  (isPriorityEditable
                    ? `Allowed range: ${priorityRange.min}-${priorityRange.max}`
                    : "Fixed at 1 for this rule type")
                }
                slotProps={{
                  input: {
                    inputProps: isPriorityEditable
                      ? { min: priorityRange.min, max: priorityRange.max }
                      : {},
                  },
                }}
              />

              <Typography sx={{ fontSize: 12, opacity: 0.7, mt: 0.5 }}>
                Rules are evaluated by priority, where a lower number indicates higher priority (Liquidation Sale &gt; Flash Sale &gt; Discounted Sale). The priority range for each rule type can be adjusted within its allowed boundaries.
              </Typography>
            </Grid>
          </Grid>
        </Section>

        {/* ===================== EFFECTIVE DATES ===================== */}
        <Section title="Effective Dates">
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Start Date
              </Typography>
              <TextField
                size="small"
                type={disableStartDate ? "text" : "date"}
                fullWidth
                value={form.startDate}
                onChange={(e) => {
                  const value = e.target.value;
                  validateDates("startDate", value);
                }}
                InputLabelProps={{ shrink: true }}
                error={!!errors?.startDate || dateError?.startDate}
                helperText={errors?.startDate || dateError?.startDate || "Date when rule becomes active"}
                slotProps={{
                  readOnly: { disableStartDate }
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 5, sm: 6 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                End Date
              </Typography>
              <TextField
                size="small"
                type={disableEndDate ? "text" : "date"}
                fullWidth
                value={form.endDate}
                onChange={(e) => {
                  const value = e.target.value;
                  validateDates("endDate", value);
                }}
                InputLabelProps={{ shrink: true }}
                error={!!errors?.endDate || dateError?.endDate}
                helperText={errors?.endDate || dateError?.endDate || "Date when rule expires"}
                slotProps={{
                  readOnly: { disableEndDate }
                }}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ===================== FREQUENCY ===================== */}
        {form.ruleType === "FLASH_SALE" &&
          <Section title="Frequency">
            <Stack spacing={2}>

              <FormControl>
                <RadioGroup
                  row
                  value={frequencyMode}
                  onChange={(e) => onFrequencyModeChange(e.target.value)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="All Days" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                </RadioGroup>
              </FormControl>

              {(allDaysMode ? daysOfWeek.slice(0, 1) : daysOfWeek).map((dayBlock, dayIndex) => (
                <Paper key={dayIndex} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="flex-end">
                    {!allDaysMode && (<Grid item size={{ xs: 12, md: 4, sm: 6 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Day
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={dayBlock.day}
                          onChange={(e) => updateDay(dayIndex, e.target.value)}
                        >
                          {allowedDayValues.map((dayValue) => {
                            const dayLabel = DAY_OPTIONS.find(d => d.value === dayValue)?.label;
                            const usedByAnotherRow = daysOfWeek.some(
                              (x, i) => i !== dayIndex && Number(x.day) === dayValue
                            );
                            return (
                              <MenuItem
                                key={dayValue}
                                value={dayValue}
                                disabled={usedByAnotherRow}
                              >
                                {dayLabel}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    )}

                    <Grid item size={{ xs: 12, md: 2, sm: 6 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => addSlot(dayIndex)}
                      >
                        Add Slot
                      </Button>
                    </Grid>

                    {!allDaysMode && (
                      <Grid item size={{ xs: 12, md: 2, sm: 6 }}>
                        <Button
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeDay(dayIndex)}
                        >
                          Remove Day
                        </Button>
                      </Grid>
                    )}
                  </Grid>

                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    {dayBlock.timeSlots.map((slot, slotIndex) => (
                      <Grid container spacing={2} key={slotIndex} alignItems="flex-start">
                        {(() => {
                          const slotError = getTimeSlotError(slot);

                          return (
                            <> <Grid item size={{ xs: 12, md: 4, sm: 6 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Start
                              </Typography>
                              <TextField
                                size="small"
                                type="time"
                                fullWidth
                                value={slot.start ? slot.start.slice(0, 5) : ""}
                                onChange={(e) =>
                                  updateSlot(dayIndex, slotIndex, "start", e.target.value)
                                }
                              />
                            </Grid>

                              <Grid item size={{ xs: 12, md: 4, sm: 6 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  End
                                </Typography>
                                <TextField
                                  size="small"
                                  type="time"
                                  fullWidth
                                  value={slot.end ? slot.end.slice(0, 5) : ""}
                                  error={!!slotError}
                                  helperText={slotError || ""}
                                  onChange={(e) =>
                                    updateSlot(dayIndex, slotIndex, "end", e.target.value)
                                  }
                                />
                              </Grid>

                              <Grid item size={{ xs: 12, md: 2, sm: 6 }} sx={{ mt: 3 }}>
                                <IconButton
                                  color="error"
                                  onClick={() => removeSlot(dayIndex, slotIndex)}
                                  aria-label="remove slot"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </>
                          );
                        })()}
                      </Grid >
                    ))}
                  </Stack>
                </Paper>
              ))}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addDay}
                  disabled={allDaysUsed || allDaysMode || frequencyMode !== "custom"}
                >
                  Add Day
                </Button>

                <Button
                  color="error"
                  variant="text"
                  onClick={clearAllDays}
                  disabled={!daysOfWeek.length}
                >
                  Clear Days
                </Button>
              </Box>
            </Stack>
          </Section>}
        {/* ===================== STOP ON MATCH ===================== */}
        {showStopOnMatch && (
          <Section title="">
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.stopOnMatch}
                  onChange={(e) => setField("stopOnMatch", e.target.checked)}
                />
              }
              label="Stop further processing on first match"
            />
          </Section>
        )}
      </CardContent>

    </Card >
  );
}
